/**
 * Inline or modal edit of property tags. Free-form text; case-insensitive.
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Tag, X } from 'lucide-react';
import { usePropertyTagsMutation } from '@/hooks/usePropertyTags';
import { toast } from 'sonner';

interface PropertyTagsEditorProps {
  propertyId: string;
  propertyName: string;
  initialTags: string[];
  onSaved?: () => void;
  /** Controlled open state when used from parent (e.g. dropdown) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When set, show as trigger button; only used when open/onOpenChange not provided */
  triggerLabel?: string;
  children?: React.ReactNode;
}

export function PropertyTagsEditor({
  propertyId,
  propertyName,
  initialTags,
  onSaved,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  triggerLabel = 'Edit tags',
  children,
}: PropertyTagsEditorProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = controlledOnOpenChange ?? setInternalOpen;

  const [tags, setTags] = useState<string[]>(initialTags);
  const [inputValue, setInputValue] = useState('');
  const mutation = usePropertyTagsMutation(propertyId);

  useEffect(() => {
    if (open) {
      setTags(initialTags);
      setInputValue('');
    }
  }, [open, initialTags]);

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  const addTag = () => {
    const t = inputValue.trim().toLowerCase();
    if (!t) return; // Prevent empty tag creation
    if (tags.includes(t)) {
      toast.info('Tag already added');
      setInputValue('');
      return;
    }
    setTags([...tags, t].sort());
    setInputValue('');
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((x) => x !== tag));
  };

  const handleSave = () => {
    const previousTags = [...tags];
    mutation.mutate(tags, {
      onSuccess: () => {
        toast.success('Tags updated');
        setOpen(false);
        onSaved?.();
      },
      onError: (e) => {
        toast.error(e instanceof Error ? e.message : 'Failed to update tags');
        setTags(previousTags); // Revert on failure so user can retry
      },
    });
  };

  const trigger = children ?? (
    <Button variant="outline" size="sm" onClick={(e) => (e.stopPropagation(), setOpen(true))}>
      <Tag className="w-4 h-4 mr-2" />
      {triggerLabel}
    </Button>
  );

  return (
    <>
      {controlledOpen == null && (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {trigger}
        </div>
      )}
      <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Tags — {propertyName}</DialogTitle>
          <DialogDescription>
            Add or remove tags. Tags are case-insensitive and used for filtering.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex gap-2">
            <Input
              placeholder="New tag"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            />
            <Button type="button" variant="secondary" size="sm" onClick={addTag}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="pr-1 gap-1">
                {tag}
                <button
                  type="button"
                  className="rounded-full hover:bg-muted p-0.5"
                  onClick={() => removeTag(tag)}
                  aria-label={`Remove ${tag}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {tags.length === 0 && (
              <span className="text-sm text-muted-foreground">No tags yet</span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </>
  );
}
